import { useEffect, useState } from 'react'
import { Outlet, Navigate, Link, useLocation } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'
import { useUIStore } from '@/stores/ui.store'
import { authApi } from '@/lib/api'
import { Sidebar } from './Sidebar'
import { ToastContainer, ErrorHistoryFab } from '@/components/ui'
import { useKmRenderResume } from '@/hooks/useKmRenderResume'
import { endpointForPath, firstAllowedPath } from '@/lib/navigation/endpointAccess'
import type { UserRole, UserSession } from '@/types'

interface MainLayoutProps {
  requiredRole?: UserRole
}

export function MainLayout({ requiredRole }: MainLayoutProps) {
  const {
    isAuthenticated,
    role,
    userId,
    permissionsConfigured,
    effectiveEndpoints,
    setAuth,
    clearAuth,
  } = useAuthStore()
  const location = useLocation()
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const [sessionChecked, setSessionChecked] = useState(false)
  const [sessionValid, setSessionValid] = useState(true)
  useKmRenderResume()

  // Validate the server session and refresh permissions on mount.
  useEffect(() => {
    if (!isAuthenticated) {
      setSessionChecked(true)
      return
    }
    setSessionChecked(false)
    authApi.check()
      .then((response) => {
        const session = response.data as UserSession
        setAuth(session.role, session.role_name, {
          userId: session.user_id,
          email: session.email,
          tenantId: session.tenant_id,
          tenantSlug: session.tenant_slug,
          allowedEndpoints: session.allowed_endpoints,
          permissionsConfigured: session.permissions_configured,
          effectiveEndpoints: session.effective_endpoints,
        })
        setSessionValid(true)
        setSessionChecked(true)
      })
      .catch((err) => {
        const status = err?.response?.status
        if (status === 401 || status === 403) {
          // Session expired or missing — clear frontend auth.
          clearAuth()
          setSessionValid(false)
        } else {
          // Network error / server not running — keep auth, show page anyway
          setSessionValid(true)
        }
        setSessionChecked(true)
      })
  }, [isAuthenticated, location.pathname, setAuth, clearAuth])

  if (!sessionChecked) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">Проверка сессии...</div>
      </div>
    )
  }

  if (!isAuthenticated || !sessionValid) return <Navigate to="/login" replace />
  if (requiredRole && role !== requiredRole) {
    return <Navigate to={role === 'admin' ? '/admin' : '/worker'} replace />
  }
  if (userId && role && permissionsConfigured) {
    const endpoint = endpointForPath(location.pathname, role)
    if (endpoint && !effectiveEndpoints?.includes(endpoint)) {
      const fallback = firstAllowedPath(role, effectiveEndpoints ?? [])
      if (fallback) return <Navigate to={fallback} replace />
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
            <h1 className="text-lg font-semibold text-gray-900">Нет доступных разделов</h1>
            <p className="mt-2 text-sm text-gray-500">
              Обратитесь к администратору платформы, чтобы получить права доступа.
            </p>
          </div>
        </div>
      )
    }
  }

  return (
    <div className="flex h-dvh flex-col bg-gray-50 md:flex-row">
      <Sidebar />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-gray-200 bg-white/95 px-3 py-2.5 backdrop-blur md:hidden">
          <button
            type="button"
            onClick={toggleSidebar}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            aria-label="Открыть меню"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link
            to={role === 'admin' ? '/admin' : '/worker/tasks'}
            className="flex min-w-0 items-center gap-2 no-underline"
          >
            <img src="/TrueMark logo.svg" alt="" className="h-6 w-auto shrink-0" />
            <span className="truncate text-sm font-semibold text-gray-900">TrueMark</span>
          </Link>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-auto">
          <div className="min-w-0 p-4 sm:p-5 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>

      <ToastContainer />
      <ErrorHistoryFab />
    </div>
  )
}
